import React, { useState, useEffect } from 'react';
import { 
  Share2, 
  Mail, 
  Table, 
  Webhook, 
  Check, 
  ExternalLink, 
  AlertCircle,
  HelpCircle,
  Zap,
  ArrowRight,
  ShieldCheck,
  Facebook,
  Instagram,
  Search,
  Video,
  LayoutGrid,
  Settings2,
  Activity,
  ChevronRight,
  Plus,
  X
} from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'motion/react';
import { useToast } from './Toast';

interface Integration {
  id: string;
  name: string;
  type: 'email' | 'sheets' | 'webhook' | 'ads';
  status: 'connected' | 'disconnected';
  config: any;
  description: string;
  icon: React.ElementType;
  tooltip: string;
}

type TabType = 'all' | 'email' | 'sheets' | 'webhook' | 'ads';

export function IntegrationsView() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'active_campaign',
      name: 'ActiveCampaign',
      type: 'email',
      status: 'disconnected',
      config: { apiUrl: '', apiKey: '' },
      description: 'Envie seus leads automaticamente para suas listas no ActiveCampaign.',
      icon: Zap,
      tooltip: 'Sincroniza novos leads diretamente com sua conta do ActiveCampaign.'
    },
    {
      id: 'email',
      name: 'Notificações por E-mail',
      type: 'email',
      status: 'disconnected',
      config: { email: '' },
      description: 'Receba alertas instantâneos sempre que um novo lead for capturado.',
      icon: Mail,
      tooltip: 'Envia um e-mail em tempo real para você ou sua equipe a cada nova conversão.'
    },
    {
      id: 'sheets',
      name: 'Google Sheets',
      type: 'sheets',
      status: 'disconnected',
      config: { spreadsheetId: '' },
      description: 'Sincronize seus leads automaticamente com uma planilha do Google.',
      icon: Table,
      tooltip: 'Exporta automaticamente os dados dos leads para uma planilha do Google Sheets.'
    },
    {
      id: 'webhook',
      name: 'Webhooks (Zapier/Make)',
      type: 'webhook',
      status: 'disconnected',
      config: { url: '' },
      description: 'Envie dados em tempo real para qualquer serviço via Webhook.',
      icon: Webhook,
      tooltip: 'Conecte o OptiVelo a milhares de outros apps usando Zapier, Make ou Pabbly.'
    },
    {
      id: 'meta_ads',
      name: 'Meta Ads (Facebook/IG)',
      type: 'ads',
      status: 'disconnected',
      config: { pixelId: '' },
      description: 'Sincronize eventos de conversão e otimize suas campanhas no Facebook.',
      icon: Facebook,
      tooltip: 'Envia eventos de conversão diretamente para o Pixel do Facebook para otimizar o ROAS.'
    },
    {
      id: 'google_ads',
      name: 'Google Ads',
      type: 'ads',
      status: 'disconnected',
      config: { conversionId: '' },
      description: 'Rastreie conversões do Google Ads e melhore seu índice de qualidade.',
      icon: Search,
      tooltip: 'Integração nativa para rastreamento de conversões e otimização de lances no Google.'
    },
    {
      id: 'tiktok_ads',
      name: 'TikTok Ads',
      type: 'ads',
      status: 'disconnected',
      config: { pixelCode: '' },
      description: 'Conecte seu Pixel do TikTok para escalar suas vendas na plataforma.',
      icon: Video,
      tooltip: 'Sincronização de eventos para campanhas de alta performance no TikTok.'
    }
  ]);

  const [loading, setLoading] = useState(false);
  const [activeIntegration, setActiveIntegration] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.currentUser) return;

    const q = query(
      collection(db, 'integrations'),
      where('userId', '==', auth.currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dbIntegrations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setIntegrations(prev => prev.map(integ => {
        const dbInteg = dbIntegrations.find((di: any) => di.type === integ.id);
        if (dbInteg) {
          return {
            ...integ,
            status: (dbInteg as any).status,
            config: (dbInteg as any).config,
            dbId: (dbInteg as any).id
          };
        }
        return integ;
      }));
    });

    return () => unsubscribe();
  }, []);

  const handleConnect = (id: string) => {
    setActiveIntegration(id);
  };

  const saveIntegration = async (id: string, config: any) => {
    if (!auth.currentUser) return;
    setLoading(true);
    
    try {
      const integ = integrations.find(i => i.id === id);
      const dbId = (integ as any).dbId;

      if (dbId) {
        await updateDoc(doc(db, 'integrations', dbId), {
          config,
          status: 'connected',
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'integrations'), {
          userId: auth.currentUser.uid,
          type: id,
          config,
          status: 'connected',
          updatedAt: serverTimestamp()
        });
      }
      
      showToast(`${integ?.name} conectado com sucesso!`, 'success');
      setActiveIntegration(null);
    } catch (error) {
      console.error("Error saving integration:", error);
      showToast('Erro ao salvar integração.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredIntegrations = activeTab === 'all' 
    ? integrations 
    : integrations.filter(i => i.type === activeTab);

  const tabs = [
    { id: 'all', label: 'Todas', icon: LayoutGrid },
    { id: 'email', label: 'E-mail & CRM', icon: Mail },
    { id: 'ads', label: 'Ads & Pixel', icon: Activity },
    { id: 'sheets', label: 'Planilhas', icon: Table },
    { id: 'webhook', label: 'Webhooks', icon: Webhook },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-12 animate-fade-in pb-20">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-[11px] font-bold uppercase tracking-widest">
            <Share2 className="w-3 h-3" />
            Ecossistema OptiVelo
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white tracking-tighter">Integrações</h1>
          <p className="text-lg text-slate-500 max-w-xl font-medium">
            Automatize seu fluxo de vendas conectando o OptiVelo às ferramentas que impulsionam seu negócio.
          </p>
        </div>
        
        <div className="flex items-center gap-4 bg-white/5 p-1.5 rounded-2xl border border-white/5">
          <div className="flex flex-col items-center px-4 py-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1">Ativas</span>
            <span className="text-xl font-bold text-white">{integrations.filter(i => i.status === 'connected').length}</span>
          </div>
          <div className="w-[1px] h-8 bg-white/10"></div>
          <div className="flex flex-col items-center px-4 py-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1">Disponíveis</span>
            <span className="text-xl font-bold text-indigo-400">{integrations.length}</span>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="flex items-center gap-2 p-1.5 bg-black/40 border border-white/5 rounded-2xl overflow-x-auto no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as TabType)}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20' 
                : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid Section */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredIntegrations.map((integ) => (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              key={integ.id}
              className={`premium-card p-6 flex flex-col h-full group relative overflow-hidden transition-all duration-500 ${
                integ.status === 'connected' ? 'border-emerald-500/20 bg-emerald-500/[0.02]' : ''
              }`}
            >
              {/* Status Indicator Background */}
              {integ.status === 'connected' && (
                <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
              )}

              <div className="flex justify-between items-start mb-6">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                  integ.status === 'connected' 
                    ? 'bg-emerald-500/10 text-emerald-500 shadow-lg shadow-emerald-500/10' 
                    : 'bg-white/5 text-slate-400 group-hover:bg-white/10 group-hover:text-white group-hover:scale-110'
                }`}>
                  <integ.icon className="w-7 h-7" />
                </div>
                
                <div className="flex items-center gap-2">
                  {integ.status === 'connected' ? (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                      <span className="text-[11px] font-bold text-emerald-500 uppercase tracking-widest">Conectado</span>
                    </div>
                  ) : (
                    <div className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-full">
                      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Desconectado</span>
                    </div>
                  )}
                  <div className="group/help relative">
                    <HelpCircle className="w-4 h-4 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                    <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-slate-900 border border-white/10 rounded-xl text-[10px] text-slate-400 opacity-0 invisible group-hover/help:opacity-100 group-hover/help:visible transition-all z-50 shadow-2xl leading-relaxed">
                      {integ.tooltip}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 space-y-3">
                <h3 className="text-lg font-bold text-white group-hover:text-indigo-400 transition-colors">{integ.name}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {integ.description}
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-white/5">
                {activeIntegration === integ.id ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                        <Settings2 className="w-3 h-3" />
                        {integ.id === 'active_campaign' ? 'API URL' :
                         integ.type === 'email' ? 'E-mail de Destino' : 
                         integ.type === 'sheets' ? 'ID da Planilha' : 
                         integ.type === 'webhook' ? 'URL do Webhook' : 
                         integ.id === 'meta_ads' ? 'Pixel ID' :
                         integ.id === 'google_ads' ? 'Conversion ID' : 'Pixel Code'}
                      </label>
                      <input 
                        type="text"
                        value={integ.id === 'active_campaign' ? integ.config.apiUrl : integ.config[Object.keys(integ.config)[0]]}
                        onChange={(e) => {
                          const newConfig = { ...integ.config };
                          const key = integ.id === 'active_campaign' ? 'apiUrl' : Object.keys(integ.config)[0];
                          newConfig[key] = e.target.value;
                          setIntegrations(prev => prev.map(i => i.id === integ.id ? { ...i, config: newConfig } : i));
                        }}
                        placeholder={integ.id === 'active_campaign' ? 'https://suaconta.api-us1.com' : integ.type === 'email' ? 'exemplo@email.com' : 'https://...'}
                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-xs text-white focus:border-indigo-500 focus:outline-none transition-all"
                      />
                    </div>
                    {integ.id === 'active_campaign' && (
                      <div className="space-y-2">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                          <ShieldCheck className="w-3 h-3" />
                          API Key
                        </label>
                        <input 
                          type="password"
                          value={integ.config.apiKey}
                          onChange={(e) => {
                            const newConfig = { ...integ.config, apiKey: e.target.value };
                            setIntegrations(prev => prev.map(i => i.id === integ.id ? { ...i, config: newConfig } : i));
                          }}
                          placeholder="Sua API Key"
                          className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-xs text-white focus:border-indigo-500 focus:outline-none transition-all"
                        />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button 
                        onClick={() => saveIntegration(integ.id, integ.config)}
                        disabled={loading}
                        className="flex-1 bg-indigo-500 hover:bg-indigo-400 text-white text-[10px] font-bold uppercase tracking-widest py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/20"
                      >
                        {loading ? 'Salvando...' : 'Salvar Configuração'}
                      </button>
                      <button 
                        onClick={() => setActiveIntegration(null)}
                        className="px-4 py-3.5 bg-white/5 hover:bg-white/10 text-slate-400 rounded-xl transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <button 
                    onClick={() => handleConnect(integ.id)}
                    className={`w-full py-3.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 group/btn ${
                      integ.status === 'connected' 
                        ? 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white' 
                        : 'bg-white text-black hover:bg-indigo-500 hover:text-white hover:scale-[1.02]'
                    }`}
                  >
                    {integ.status === 'connected' ? (
                      <>
                        <Settings2 className="w-3.5 h-3.5" />
                        Gerenciar
                      </>
                    ) : (
                      <>
                        <Plus className="w-3.5 h-3.5" />
                        Conectar Agora
                      </>
                    )}
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-1 ${integ.status === 'connected' ? 'hidden' : ''}`} />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Security Banner */}
      <div className="premium-card p-10 bg-indigo-500/[0.03] border-indigo-500/10 flex flex-col md:flex-row items-center gap-12 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-500/5 to-transparent pointer-events-none"></div>
        
        <div className="flex-1 space-y-6 relative z-10">
          <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center text-indigo-400 shadow-inner">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <div className="space-y-3">
            <h2 className="text-3xl font-bold text-white tracking-tight">Segurança de Dados Nível Enterprise</h2>
            <p className="text-slate-400 leading-relaxed max-w-2xl">
              Todas as integrações do OptiVelo utilizam criptografia de ponta a ponta e protocolos OAuth2 para garantir que seus dados e de seus clientes estejam sempre protegidos.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-6 pt-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg border border-white/5">
              <Check className="w-4 h-4 text-emerald-500" />
              <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg border border-white/5">
              <Check className="w-4 h-4 text-emerald-500" />
              <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">SSL 256-bit</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg border border-white/5">
              <Check className="w-4 h-4 text-emerald-500" />
              <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">OAuth 2.0</span>
            </div>
          </div>
        </div>

        <div className="w-full md:w-80 p-8 bg-black/60 backdrop-blur-xl border border-white/10 rounded-[40px] space-y-8 relative z-10 shadow-2xl">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Status da API</p>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                <span className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest">Online</span>
              </div>
            </div>
            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden p-[1px]">
              <div className="h-full w-[98%] bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-full"></div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Uptime</p>
              <p className="text-xl font-bold text-white tracking-tighter">99.99%</p>
            </div>
            <div className="space-y-1">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Latência</p>
              <p className="text-xl font-bold text-white tracking-tighter">42ms</p>
            </div>
          </div>

          <button className="w-full py-3.5 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 rounded-2xl text-[10px] font-bold text-indigo-400 uppercase tracking-widest transition-all flex items-center justify-center gap-2 group">
            Documentação API
            <ExternalLink className="w-3 h-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
